import { z } from 'zod';

/**
 * Shared primitive schemas. Money is always integer minor units (§ AGENTS.md contracts).
 * Timestamps are ISO 8601 UTC; timezones are stored separately as IANA identifiers (§28.1).
 */

/** ISO 8601 UTC instant, e.g. "2026-08-12T20:15:30Z". */
export const IsoTimestamp = z
  .string()
  .datetime({ offset: false, message: 'Must be an ISO 8601 UTC timestamp (…Z)' });
export type IsoTimestamp = z.infer<typeof IsoTimestamp>;

/** IANA timezone identifier, e.g. "America/Toronto". Validated against the runtime tz database. */
export const IanaTimezone = z.string().refine(
  (tz) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Must be a valid IANA timezone identifier' },
);
export type IanaTimezone = z.infer<typeof IanaTimezone>;

/** ISO 4217 currency code, uppercase. The allowlist is enforced at the payments boundary. */
export const CurrencyCode = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/, 'Must be a 3-letter ISO 4217 currency code');
export type CurrencyCode = z.infer<typeof CurrencyCode>;

/** Money in integer minor units (cents). Never a float. */
export const MinorUnits = z.number().int();
export type MinorUnits = z.infer<typeof MinorUnits>;

export const Money = z.object({
  currency: CurrencyCode,
  amountMinor: MinorUnits.nonnegative(),
});
export type Money = z.infer<typeof Money>;

/** URL-safe slug for artists and workspaces. */
export const Slug = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Must be a lowercase URL-safe slug');
export type Slug = z.infer<typeof Slug>;

/** Short public code used in audience URLs. Non-sequential, non-sensitive (§7, §14). */
export const PublicCode = z
  .string()
  .min(6)
  .max(24)
  .regex(/^[A-Za-z0-9]+$/, 'Public code must be alphanumeric');
export type PublicCode = z.infer<typeof PublicCode>;

/** QR placement source code — allowlisted characters, no personal data (§26.3). */
export const SourceCode = z
  .string()
  .min(1)
  .max(32)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Source code must be lowercase alphanumeric with hyphens');
export type SourceCode = z.infer<typeof SourceCode>;

/**
 * Safe public URL: HTTPS only (§16). Rejects javascript:, data:, and other unsafe schemes.
 * `mailto:` is validated separately via {@link MailtoUrl}.
 */
export const HttpsUrl = z
  .string()
  .url()
  .refine((v) => {
    try {
      return new URL(v).protocol === 'https:';
    } catch {
      return false;
    }
  }, 'Must be an https:// URL');
export type HttpsUrl = z.infer<typeof HttpsUrl>;

export const MailtoUrl = z
  .string()
  .refine((v) => v.startsWith('mailto:') && v.length > 'mailto:'.length, 'Must be a mailto: URL');
export type MailtoUrl = z.infer<typeof MailtoUrl>;

/** A public link that is either https:// or mailto: (§16 link rules). */
export const SafeLink = z.union([HttpsUrl, MailtoUrl]);
export type SafeLink = z.infer<typeof SafeLink>;

/** Optimistic-concurrency version counter (§28.1). */
export const Version = z.number().int().nonnegative();
export type Version = z.infer<typeof Version>;

/** Idempotency key supplied by clients on consequential POSTs (§28.1). */
export const IdempotencyKey = z.string().min(8).max(200);
export type IdempotencyKey = z.infer<typeof IdempotencyKey>;

/** Hex color token, e.g. "#1a1a1a". Theme tokens only; no arbitrary CSS (§16). */
export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be a 6-digit hex color');
export type HexColor = z.infer<typeof HexColor>;

/** Timestamps embedded in most persisted entities. */
export const Timestamps = z.object({
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
});
export type Timestamps = z.infer<typeof Timestamps>;

/** Cursor for keyset pagination (§28.1). Opaque base64url token. */
export const Cursor = z.string().min(1);
export type Cursor = z.infer<typeof Cursor>;

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: Cursor.nullable(),
  });
}
